/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useIntl} from 'react-intl'
import {
    Box,
    Button,
    IconButton,
    Flex,
    Text,
    useMultiStyleConfig,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ChevronLeftIcon, BrandLogo} from '../../../components/custom-icons'
import HelpModal from '../../../pages/checkout/partials/help-modal'
import {HOME_HREF} from '../../../constants'

// Custom hooks
import {useCurrentBasket} from '../../../hooks/use-current-basket'

/**
 * Checkout Header
 * @returns {JSX.Element}
 */
const CheckoutHeader = () => {
    const intl = useIntl()
    const {
        derivedData: {totalItems}
    } = useCurrentBasket()

    const styles = useMultiStyleConfig('Header')

    const {isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose} = useDisclosure()

    return (
        <Box px={[4, 4, 6]} bg="white">
            <Box maxWidth="container.xxxl" marginLeft="auto" marginRight="auto">
                <Flex h={{base: '64px', md: '52px'}} align="center" justify="space-between">
                    {/** Cart Link */}
                    <IconButton
                        as={Link}
                        href="/cart"
                        display="inline-flex"
                        variant="unstyled"
                        color="gray.900"
                        size="auto"
                        px={['6px', null, '14px']}
                        icon={<ChevronLeftIcon boxSize={'12px'} />}
                        aria-label={intl.formatMessage(
                            {
                                id: 'checkout_header.link.assistive_msg.cart',
                                defaultMessage: 'Back to cart, number of items: {numItems}'
                            },
                            {numItems: totalItems}
                        )}
                    />
                    {/** Logo */}
                    <Link
                        href={HOME_HREF}
                        title={intl.formatMessage({
                            defaultMessage: 'Back to homepage',
                            id: 'checkout_header.back'
                        })}
                        position="absolute"
                        mx="auto"
                        left="50%"
                        transform="translateX(-50%)"
                    >
                        <BrandLogo {...styles.logo} />
                    </Link>
                    {/** Help Link */}
                    <Button
                        type="button"
                        variant={'link'}
                        textDecoration="none"
                        p={['6px', null, '14px']}
                        {...styles.links}
                        onClick={onModalOpen}
                    >
                        <Text variant="descriptor">
                            {intl.formatMessage({
                                id: 'header.checkout.button.help',
                                defaultMessage: 'Help?'
                            })}
                        </Text>
                    </Button>
                </Flex>
            </Box>
            <HelpModal isOpen={isModalOpen} onClose={onModalClose} />
        </Box>
    )
}

export default CheckoutHeader
